<template>
  <ol
    class="wrapper stack"
    :style="{
      justifyItems: horizontal === 'left' ? 'start' : horizontal === 'center' ? 'center' : 'end',
      alignItems: vertical === 'top' ? 'start' : 'end',
    }"
  >
    <li
      v-for="(item, i) in items"
      :key="item.id"
      class="item"
      :class="[`align-${horizontal}`, `justify-${vertical}`, color]"
      :style="{
        translate: `${i * offsetX}px ${i * offsetY}px`,
        scale: `${props.horizontal === 'center' ? 1 - i * 0.05 : 1}`,
        opacity: `${1 - i * 0.35}`,
        pointerEvents: i === 0 ? 'auto' : 'none',
        zIndex: items.length - i,
      }"
      @click="
        () => {
          clear(item.id)
          time.reset()
        }
      "
      @mouseenter="time.stop"
      @mouseleave="time.start"
    >
      {{ item.message }}
      <div v-if="i === 0" class="progress-bar" :style="{ width: progressWidth }"></div>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { useCountdown } from '@/lib/countdown'
import { clear, queue } from '@/lib/logging'
import { computed, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    items: typeof queue.value
    timeout?: number
    horizontal?: 'left' | 'center' | 'right'
    vertical?: 'top' | 'bottom'
    color?: string
  }>(),
  {
    timeout: 6000,
    horizontal: 'right',
    vertical: 'bottom',
    color: '',
  }
)

const gap = 10

const offsetX = computed(() => {
  return props.horizontal === 'left' ? gap : props.horizontal === 'center' ? 0 : -gap
})
const offsetY = computed(() => {
  return props.vertical === 'top' ? gap : -gap
})

const time = useCountdown(props.timeout, {
  onComplete: () => clear(props.items[0]?.id),
})
const progressWidth = computed(() => {
  if (props.items.length === 0 || time.remaining.value === 0) return '0%'
  return `${(time.remaining.value / props.timeout) * 100}%`
})
watch(
  () => props.items,
  (items) => (items.length ? time.start() : time.reset()),
  { immediate: true }
)
</script>

<style scoped>
.stack {
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  justify-items: start;
  align-items: start;

  > * {
    grid-row: 1 / 2;
    grid-column: 1 / 2;
  }
}
.wrapper {
  position: fixed;
  inset: 0;
  justify-items: end;
  align-items: end;
  pointer-events: none;
  padding: 1.5rem;
  z-index: var(--z-notifications);
}
.item {
  pointer-events: auto;
  background: #234;
  color: #fff;
  border-radius: 4px;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
  max-width: 400px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 0.5rem 2rem 0rem rgba(0, 0, 0, 0.25);
  transition: all 0.3s;
  position: relative;
}
@starting-style {
  .item {
    transform: translateY(20px);
    opacity: 0;
  }
}
.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  pointer-events: none;
  background: white;
  mix-blend-mode: overlay;
  transition: width 0.1s linear;
}
</style>
